import { useCallback, useState } from 'react';

const STORAGE_PREFIX = 'moftakilab:columns:';
// 0.3.4 — FEAT-2026-0018 added a "Location ID" column to the samples grid; bump
// so users with saved sample-grid prefs pick up the new (default-visible) column.
const PREFS_VERSION = '0.3.4';

// Clear stale column preferences when version changes (e.g. column renames)
const VERSION_KEY = `${STORAGE_PREFIX}version`;
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem(VERSION_KEY);
  if (stored !== PREFS_VERSION) {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(STORAGE_PREFIX) && k !== VERSION_KEY);
    keys.forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(VERSION_KEY, PREFS_VERSION);
  }
}

export interface ColumnVisibility {
  field: string;
  headerName: string;
  visible: boolean;
  mandatory?: boolean;
}

export function useColumnPreferences(
  gridId: string,
  allColumns: { field: string; headerName: string }[],
  defaultVisible: string[],
  mandatoryFields: string[] = [],
) {
  const storageKey = `${STORAGE_PREFIX}${gridId}`;

  const loadSaved = (): string[] | null => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const [visibleFields, setVisibleFields] = useState<string[]>(
    () => loadSaved() ?? defaultVisible,
  );

  const persist = (fields: string[]) => {
    localStorage.setItem(storageKey, JSON.stringify(fields));
  };

  const toggleColumn = useCallback(
    (field: string) => {
      if (mandatoryFields.includes(field)) return;
      setVisibleFields((prev) => {
        const next = prev.includes(field)
          ? prev.filter((f) => f !== field)
          : [...prev, field];
        persist(next);
        return next;
      });
    },
    [mandatoryFields, storageKey],
  );

  const resetDefaults = useCallback(() => {
    setVisibleFields(defaultVisible);
    persist(defaultVisible);
  }, [defaultVisible, storageKey]);

  const columnVisibility: ColumnVisibility[] = allColumns.map((col) => ({
    field: col.field,
    headerName: col.headerName,
    visible: visibleFields.includes(col.field),
    mandatory: mandatoryFields.includes(col.field),
  }));

  return { visibleFields, columnVisibility, toggleColumn, resetDefaults };
}
